import {
  OfferProductDto,
  OfferProductSet,
} from "@intelligems/ig-types/dtos/offer-products";
import {
  ProductOptionFe,
  VariantOptionsFe,
} from "@intelligems/ig-types/fe-only/offers";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { IOfferProductsState } from "./types";

const initialState: IOfferProductsState = {
  isSuccess: false,
  offerProducts: [],
  storedOfferProducts: [],
  isFetching: false,
  isLoading: false,
  productOptions: {},
  variantOptions: {},
};

const offerProductsSlice = createSlice({
  name: "offerProducts",
  initialState,
  reducers: {
    resetOfferProducts: (draft) => {
      draft.isSuccess = false;
      draft.offerProducts = [];
      draft.storedOfferProducts = [];
      draft.productOptions = {};
      draft.variantOptions = {};
    },
    resetOfferProductsToStored: (draft) => {
      //@TODO this is not what the function name suggests
      draft.offerProducts = [];
    },
    initOfferProducts: (
      draft,
      action: PayloadAction<
        Pick<
          IOfferProductsState,
          "isSuccess" | "isLoading" | "isFetching" | "offerProducts"
        >
      >
    ) => {
      const { isSuccess, isLoading, isFetching, offerProducts } =
        action.payload;

      draft.isSuccess = isSuccess;
      draft.isLoading = isLoading;
      draft.isFetching = isFetching;
      if (isSuccess && !isLoading && !isFetching) {
        draft.offerProducts = offerProducts;
        draft.storedOfferProducts = offerProducts;
      }
    },
    setVariationOfferProducts: (
      draft,
      action: PayloadAction<{
        variationId: string;
        set: OfferProductSet;
        products: OfferProductDto[];
      }>
    ) => {
      if (!draft.offerProducts) {
        return;
      }

      const { variationId, set } = action.payload;
      const filteredProducts = draft.offerProducts.filter(
        (product) => product.variationId !== variationId || product.set !== set
      );
      draft.offerProducts = [
        ...filteredProducts,
        ...action.payload.products.map((product) => ({ ...product, set })),
      ];
    },
    setSelectedOfferVariationProducts: (
      draft,
      action: PayloadAction<{
        variationId: string;
        offerId: string;
        offerProducts: OfferProductDto[];
      }>
    ) => {
      const updatedDraft = draft.offerProducts.filter(
        (prod) => prod.variationId !== action.payload.variationId
      );
      const dupOfferProducts: OfferProductDto[] =
        action.payload.offerProducts.map((product) => {
          return {
            ...product,
            offerId: action.payload.offerId,
            variationId: action.payload.variationId,
          };
        });

      draft.offerProducts = [...updatedDraft, ...dupOfferProducts];
    },
    resetVariationOfferProducts: (
      draft,
      action: PayloadAction<{
        variationId: string;
      }>
    ) => {
      if (!draft.offerProducts) {
        return;
      }

      draft.offerProducts = draft.offerProducts.filter(
        (product) => product.variationId !== action.payload.variationId
      );
    },
    removeVariationOfferProduct: (
      draft,
      action: PayloadAction<{
        variationId: string;
        set: OfferProductSet;
        productId: string;
      }>
    ) => {
      if (!draft.offerProducts) {
        return;
      }
      const { variationId, set, productId } = action.payload;

      draft.offerProducts = draft.offerProducts.filter(
        (p) =>
          p.variationId !== variationId || p.set !== set || p.id !== productId
      );
    },
    removeVariationOfferProductVariant: (
      draft,
      action: PayloadAction<{
        variationId: string;
        set: OfferProductSet;
        productId: string;
        variantId: string;
      }>
    ) => {
      if (!draft.offerProducts) {
        return;
      }
      const { variationId, set, productId, variantId } = action.payload;

      const product = draft.offerProducts.find(
        (p) =>
          p.variationId === variationId && p.set === set && p.id === productId
      );

      if (!product) {
        return;
      }

      const variant = product.variants.find((v) => v.id === variantId);
      if (variant) {
        variant.inclusionType = "exclude";
      }
    },
    setProductOptions: (
      draft,
      action: PayloadAction<{
        productId: string;
        options: ProductOptionFe[];
      }>
    ) => {
      const { productId, options } = action.payload;
      draft.productOptions[productId] = options;
    },
    setVariantOptions: (
      draft,
      action: PayloadAction<{
        productId: string;
        variantOptions: VariantOptionsFe[];
      }>
    ) => {
      const { productId, variantOptions } = action.payload;
      draft.variantOptions[productId] = variantOptions;
    },
  },
  selectors: {
    getOfferProducts: (state) => state.offerProducts,
    areOfferProductsLoading: (state) => state.isLoading,
    areOfferProductsLoaded: (state) => state.isSuccess,
    getProductOptions: (state) => state.productOptions,
    getVariantOptions: (state) => state.variantOptions,
  },
});

export const {
  resetOfferProducts,
  initOfferProducts,
  removeVariationOfferProduct,
  removeVariationOfferProductVariant,
  setVariationOfferProducts,
  setSelectedOfferVariationProducts,
  resetOfferProductsToStored,
  resetVariationOfferProducts,
  setProductOptions,
  setVariantOptions,
} = offerProductsSlice.actions;

export const offerProductsReducer = offerProductsSlice.reducer;

export const {
  getOfferProducts,
  areOfferProductsLoading,
  areOfferProductsLoaded,
  getProductOptions,
  getVariantOptions,
} = offerProductsSlice.selectors;
